import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { ConfigurationService } from '@treasury/core/config';
import { LoggingService } from '@treasury/core/logging';
import { NavigationService } from '@treasury/core/navigation';
import { RemoteDepositCaptureService } from '@treasury/domain/remote-deposit-capture';
import { RemoteDepositError } from '@treasury/domain/remote-deposit-capture/remote-deposit-error';
import { NotificationService, TmBaseComponent } from '@treasury/presentation';
import { arrowRightIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-action-button';
import '@treasury/presentation/components/tm-blocking-loader';
import '@treasury/presentation/components/tm-bottom-sheet';
import { css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { HeaderBarService } from '../../services/jhd-header-bar.service';
import '../partials/deposit-detail';
import { DepositVm } from '../view-models';
import './deposit-container';

export const tagName = 'deposit-activity-container';
@customElement(tagName)
export class DepositActivityContainer extends TmBaseComponent {
    @InjectProperty()
    declare private readonly headerService: HeaderBarService;

    @InjectProperty()
    declare private readonly navService: NavigationService;

    @InjectProperty()
    declare private readonly config: ConfigurationService;

    @InjectProperty()
    declare private readonly logService: LoggingService;

    @InjectProperty()
    declare private readonly remoteDepositCaptureService: RemoteDepositCaptureService;

    @InjectProperty()
    declare private readonly notificationService: NotificationService;

    @state()
    private deposits: DepositVm[] = [];

    @state()
    private selectedDeposit?: DepositVm;

    @state()
    private depositDetailOpen = false;

    @state()
    private loading = true;

    protected async firstUpdated() {
        this.headerService.configure({ title: 'Deposits' });
        try {
            const deposits = (await this.remoteDepositCaptureService.getDeposits()) ?? [];
            this.deposits = deposits.map(deposit => new DepositVm(deposit));
        } catch (err) {
            this.logService.logError(
                'Remote Deposit Error - deposit activity container',
                err as RemoteDepositError
            );
            this.notificationService.renderError(err as RemoteDepositError);
        } finally {
            this.loading = false;
        }
    }

    protected render() {
        if (this.loading) {
            return html`<tm-blocking-loader></tm-blocking-loader>`;
        }
        return html`
            ${this.renderRouteActionItem()}
            <tm-list-layout
                .columns=${[
                    {
                        renderer: this.renderStatusIconColumn.bind(this),
                        options: { shrink: true },
                    },
                    this.renderActivityColumn.bind(this),
                    this.renderAmountColumn.bind(this),
                ]}
                .headerTitle=${'Deposit Activity'}
                .items=${this.deposits}
                .itemsDescriptor=${'deposits'}
                .groupBy=${'formattedDepositDate'}
                .searchFields=${['locationName', 'statusDescription', 'formattedTotalAmount']}
                @select-list-item=${(e: CustomEvent<DepositVm>) => {
                    this.selectedDeposit = e.detail;
                    if (e.detail.isOpen) {
                        this.navService.navigate(`/edit-deposit/${e.detail.batchReference}`, {
                            deposit: e.detail,
                        });
                    } else {
                        this.depositDetailOpen = true;
                    }
                }}
            ></tm-list-layout>
            <tm-bottom-sheet
                .open=${this.depositDetailOpen}
                @close=${() => (this.depositDetailOpen = false)}
            >
                <span slot="header-center">Deposit Detail</span>
                <deposit-detail .deposit=${this.selectedDeposit as DepositVm}></deposit-detail
            ></tm-bottom-sheet>
        `;
    }

    private renderRouteActionItem() {
        if (!this.remoteDepositCaptureService.isAuthenticated) {
            return nothing;
        }
        return html`
            <tm-action-button
                class="mt-[8px] mb-[8px]"
                .buttonTitle=${'Create a Deposit'}
                @click=${() => {
                    this.navService.navigate(`/create-deposit`);
                }}
            ></tm-action-button>
        `;
    }

    private renderStatusIconColumn(deposit: DepositVm) {
        return html`<div class="flex flex-col w-6 mr-1">
            <span class="status-icon"
                ><tm-icon .icon=${deposit.statusIcon} .color=${deposit.statusColor}></tm-icon
            ></span>
        </div>`;
    }

    private renderActivityColumn(deposit: DepositVm) {
        return html`<div class="flex flex-col">
            <span class="font-medium text-sm overflow-hidden text-ellipsis"
                >${deposit.locationName}</span
            >
            <small class="account-number text-xs" style="color: ${deposit.statusColor};"
                >${deposit.statusDescription}</small
            >
        </div>`;
    }

    private renderAmountColumn(deposit: DepositVm) {
        return html`<div class="amount-column flex flex-col">
            <div class="available-balance text-lg text-right font-medium">
                ${deposit.formattedTotalAmount}
                <tm-icon .icon=${arrowRightIcon} .color=${'var(--tm-color-primary)'}></tm-icon>
            </div>
        </div>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    overflow-y: hidden;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: DepositActivityContainer;
    }
}
